import { useState } from 'react';
import styled from 'styled-components';
import { MarkdownEditor } from '../../molecules/MarkdownEditor';
import { Modal } from '../../molecules/Modal';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background-color: #5a6268;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #007bff;
  color: white;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 13px;
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
`;

const AddObservationBehaviorModal = ({ isOpen, onClose, onSubmit, studentId, userId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // 에러 메시지 제거
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({ ...prev, description: value }));

    // 에러 메시지 제거
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '행동 이름을 입력해주세요.';
    }

    if (!formData.description.trim()) {
      newErrors.description = '행동 설명을 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await onSubmit(studentId, {
        name: formData.name,
        description: formData.description,
        createdBy: userId
      });

      // 폼 초기화
      setFormData({ name: '', description: '' });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to add observation behavior:', error);
      setErrors({ submit: error.message || '관찰 항목 추가에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // 폼 초기화
    setFormData({ name: '', description: '' });
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="새 관찰 항목 추가">
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>행동 이름 *</Label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="예: 부모를 때리는 행동"
            disabled={loading}
          />
          {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          <HelpText>관찰하려는 행동의 이름을 입력하세요.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>행동 설명 *</Label>
          <MarkdownEditor
            value={formData.description}
            onChange={handleDescriptionChange}
            height={400}
            placeholder='행동에 대한 자세한 설명을 작성하세요...\n\n예시:\n# 설명\n부모에게 손이나 발로 때리는 행동\n\n## 기록 기준\n- 의도적인 타격만 기록\n- 우발적인 접촉은 제외'
            folder="observation-images"
          />
          {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
        </FormGroup>

        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

        <ButtonGroup>
          <CancelButton type="button" onClick={handleClose} disabled={loading}>
            취소
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? '추가 중...' : '관찰 항목 추가'}
          </SubmitButton>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default AddObservationBehaviorModal;
